// Standalone HTML summary of the firm's position, for circulation to all staff
// under the Employee Share Scheme.
//
// CONTAINS RENDERED AGGREGATES ONLY. The builder takes a flat structure of
// numbers and short labels — never `data` or `rawData`, never a project,
// person, principal, scope, allocation, leave or invoicing record — so "the
// dataset cannot ship inside the file" is a property of this signature rather
// than a claim about its body.
//
// Charts are drawn as inline SVG from those numbers: no scripts, no images, no
// base64, no fonts, nothing to fetch. Opens offline, prints cleanly, and every
// figure stays selectable text.

const PALETTE = {
  paper: "#FAF9F6",
  ink: "#0A0A0A",
  muted: "#6E6E64",
  soft: "#9A958A",
  rule: "#E2DFD7",
  grid: "#EFEDE7",
  total: "#2F5AA8",
  net: "#A8342B",
  subs: "#2C6E49",
  backlog: "#0A0A0A",
  pipeline: "#C4B58A",
  invoiced: "#5B7A66",
};

const money = (n) =>
  `$${Math.round(Number.isFinite(n) ? n : 0).toLocaleString("en-NZ")}`;

const compact = (n) => {
  const v = Math.round(Number.isFinite(n) ? n : 0);
  const a = Math.abs(v);
  if (a >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (a >= 10_000) return `$${(v / 1_000).toFixed(0)}k`;
  if (a >= 1_000) return `$${(v / 1_000).toFixed(1)}k`;
  return `$${v}`;
};

const pct = (n) => `${Math.round((Number.isFinite(n) ? n : 0) * 100)}%`;

const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const stamp = (d) =>
  `${d.toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" })} · ` +
  `${d.toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit", hour12: false })}`;

// ── chart geometry ─────────────────────────────────────────────────────────
const CW = 680;
const CH = 240;
const PAD = { t: 10, r: 10, b: 26, l: 62 };
const PW = CW - PAD.l - PAD.r;
const PH = CH - PAD.t - PAD.b;

const niceCeil = (v) => {
  if (v <= 0) return 0;
  const mag = 10 ** Math.floor(Math.log10(v));
  return Math.ceil(v / mag) * mag;
};

function scale(lo, hi) {
  const min = Math.min(0, lo);
  const max = Math.max(niceCeil(hi), min === 0 ? 1 : 0);
  const span = max - min || 1;
  return {
    min,
    max,
    y: (v) => PAD.t + PH - ((v - min) / span) * PH,
    ticks: [0, 0.25, 0.5, 0.75, 1].map((f) => min + f * span),
  };
}

const bandX = (i, n) => PAD.l + (PW / n) * (i + 0.5);

function frame(sc, labels, testid) {
  const n = labels.length;
  const grid = sc.ticks
    .map(
      (t) =>
        `<line x1="${PAD.l}" x2="${CW - PAD.r}" y1="${sc.y(t).toFixed(1)}" y2="${sc.y(t).toFixed(1)}" class="g"/>` +
        `<text x="${PAD.l - 8}" y="${(sc.y(t) + 3).toFixed(1)}" class="ax" text-anchor="end">${esc(compact(t))}</text>`
    )
    .join("");
  const xl = labels
    .map(
      (l, i) =>
        `<text x="${bandX(i, n).toFixed(1)}" y="${CH - 8}" class="ax" text-anchor="middle">${esc(l)}</text>`
    )
    .join("");
  return { open: `<svg viewBox="0 0 ${CW} ${CH}" class="chart" role="img" data-block="${testid}">${grid}`, close: `${xl}</svg>` };
}

const poly = (pts) => pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");

function lineChart({ labels, series, band }) {
  const all = series.flatMap((s) => s.values).concat(band ? band.flatMap((b) => b) : []);
  const sc = scale(Math.min(...all, 0), Math.max(...all, 0));
  const n = labels.length;
  const f = frame(sc, labels, "monthly-revenue");
  let out = f.open;
  if (band) {
    const top = band.map((b, i) => [bandX(i, n), sc.y(b[1])]);
    const bottom = band.map((b, i) => [bandX(i, n), sc.y(b[0])]).reverse();
    out += `<polygon points="${poly(top.concat(bottom))}" fill="${PALETTE.subs}" fill-opacity="0.16"/>`;
  }
  for (const s of series) {
    const pts = s.values.map((v, i) => [bandX(i, n), sc.y(v)]);
    out += `<polyline points="${poly(pts)}" fill="none" stroke="${s.color}" stroke-width="1.8"/>`;
    out += pts
      .map(([x, y]) => `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="2" fill="${s.color}"/>`)
      .join("");
  }
  return out + f.close;
}

function groupedBars({ labels, series, testid }) {
  const all = series.flatMap((s) => s.values);
  const sc = scale(Math.min(...all, 0), Math.max(...all, 0));
  const n = labels.length;
  const f = frame(sc, labels, testid);
  const slot = PW / n;
  const bw = Math.min(13, (slot - 6) / series.length);
  let out = f.open;
  series.forEach((s, si) => {
    s.values.forEach((v, i) => {
      const x = bandX(i, n) - (bw * series.length) / 2 + si * bw;
      const y0 = sc.y(0);
      const y = sc.y(v);
      out += `<rect x="${x.toFixed(1)}" y="${Math.min(y, y0).toFixed(1)}" width="${bw.toFixed(1)}" height="${Math.max(1, Math.abs(y0 - y)).toFixed(1)}" fill="${s.color}"/>`;
    });
  });
  return out + f.close;
}

function barsWithLine({ labels, bars, line, testid }) {
  const all = bars.values.concat(line.values);
  const sc = scale(Math.min(...all, 0), Math.max(...all, 0));
  const n = labels.length;
  const f = frame(sc, labels, testid);
  const bw = Math.min(16, PW / n - 6);
  let out = f.open;
  bars.values.forEach((v, i) => {
    const y0 = sc.y(0);
    const y = sc.y(v);
    out += `<rect x="${(bandX(i, n) - bw / 2).toFixed(1)}" y="${Math.min(y, y0).toFixed(1)}" width="${bw.toFixed(1)}" height="${Math.max(1, Math.abs(y0 - y)).toFixed(1)}" fill="${bars.color}"/>`;
  });
  const pts = line.values.map((v, i) => [bandX(i, n), sc.y(v)]);
  out += `<polyline points="${poly(pts)}" fill="none" stroke="${line.color}" stroke-width="1.5"/>`;
  out += pts.map(([x, y]) => `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="2" fill="${line.color}"/>`).join("");
  return out + f.close;
}

function stackedBars({ labels, keys, rows, colors, testid }) {
  const pos = rows.map((r) => keys.reduce((s, k) => s + Math.max(0, r[k] || 0), 0));
  const neg = rows.map((r) => keys.reduce((s, k) => s + Math.min(0, r[k] || 0), 0));
  const sc = scale(Math.min(...neg, 0), Math.max(...pos, 0));
  const n = labels.length;
  const f = frame(sc, labels, testid);
  const bw = Math.min(26, PW / n - 6);
  let out = f.open;
  rows.forEach((r, i) => {
    let up = 0;
    let down = 0;
    for (const k of keys) {
      const v = r[k] || 0;
      if (!v) continue;
      const from = v > 0 ? up : down;
      const to = from + v;
      const yA = sc.y(Math.max(from, to));
      const yB = sc.y(Math.min(from, to));
      out += `<rect x="${(bandX(i, n) - bw / 2).toFixed(1)}" y="${yA.toFixed(1)}" width="${bw.toFixed(1)}" height="${Math.max(1, yB - yA).toFixed(1)}" fill="${colors[k] || PALETTE.soft}"/>`;
      if (v > 0) up = to;
      else down = to;
    }
  });
  return out + f.close;
}

function donut(slices, colors) {
  const total = slices.reduce((s, x) => s + Math.max(0, x.value), 0) || 1;
  const R = 86;
  const r = 54;
  const cx = 100;
  const cy = 100;
  let a0 = -Math.PI / 2;
  const arcs = slices
    .map((s) => {
      const frac = Math.max(0, s.value) / total;
      if (frac <= 0) return "";
      const a1 = a0 + frac * Math.PI * 2;
      const big = a1 - a0 > Math.PI ? 1 : 0;
      const p = (rad, ang) => `${(cx + rad * Math.cos(ang)).toFixed(2)},${(cy + rad * Math.sin(ang)).toFixed(2)}`;
      const d = `M ${p(R, a0)} A ${R} ${R} 0 ${big} 1 ${p(R, a1)} L ${p(r, a1)} A ${r} ${r} 0 ${big} 0 ${p(r, a0)} Z`;
      a0 = a1;
      return `<path d="${d}" fill="${colors[s.name]}" stroke="#fff" stroke-width="2"/>`;
    })
    .join("");
  return `<svg viewBox="0 0 200 200" class="donut" role="img" data-block="contract-status">${arcs}</svg>`;
}

const swatch = (color, label, shape = "rect") =>
  `<span class="key"><span class="sw ${shape === "line" ? "swl" : ""}" style="background:${color}"></span>${esc(label)}</span>`;

// ── document ───────────────────────────────────────────────────────────────
export function buildFirmSummaryHtml({
  scenarioLabel,
  periodLabel,
  monthCount,
  generatedAt,
  kpis,
  revenue,
  backlogPipeline,
  invoiced,
  stage,
  contract,
}) {
  const totalLabel = `${scenarioLabel} total`;
  const figures = [
    ["Total backlog", money(kpis.backlog), "Signed work — contract confirmed"],
    ["Pipeline value", money(kpis.pipeline), "Tentative work — not yet signed"],
    [totalLabel, money(kpis.scenarioTotal), `Fees across the ${monthCount}-month window`],
    ["Live projects", String(kpis.liveProjects), "Projects not marked completed"],
    ["Avg utilisation", pct(kpis.avgUtilisation), "Team capacity used, annual leave included"],
    ["Over-allocated person-months", String(kpis.overPersonMonths), "Person-months booked above 100%"],
  ];

  const labels = revenue.rows.map((r) => r.label);
  const contractTotal = contract.reduce((s, c) => s + Math.max(0, c.value), 0) || 1;
  const contractColors = { Yes: "#A8C0AE", Tentative: "#D9CDB0", No: "#C99B95" };
  const contractName = { Yes: "Confirmed", Tentative: "Tentative", No: "No" };

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Monk Mackenzie · Firm summary · ${esc(periodLabel)}</title>
<style>
  :root{--paper:${PALETTE.paper};--ink:${PALETTE.ink};--muted:${PALETTE.muted};--soft:${PALETTE.soft};--rule:${PALETTE.rule}}
  *{box-sizing:border-box}
  html,body{margin:0;padding:0;background:var(--paper);color:var(--ink)}
  body{font-family:Georgia,"Times New Roman",Times,serif;-webkit-font-smoothing:antialiased;padding:64px 28px 96px;
       -webkit-print-color-adjust:exact;print-color-adjust:exact}
  .sheet{max-width:760px;margin:0 auto}
  .mono,.n,.ax,.key,.v,table{font-family:ui-monospace,"SF Mono",SFMono-Regular,Menlo,Consolas,monospace}
  .eyebrow{font-family:ui-monospace,"SF Mono",SFMono-Regular,Menlo,Consolas,monospace;font-size:10px;letter-spacing:.16em;text-transform:uppercase;color:var(--muted)}
  h1{font-size:34px;line-height:1.1;font-weight:400;margin:14px 0 0;letter-spacing:-.01em}
  h2{font-size:19px;font-weight:400;margin:0 0 3px}
  .lede{font-size:15px;color:var(--muted);margin-top:12px;max-width:46em}
  .meta{display:grid;grid-template-columns:repeat(3,1fr);border:1px solid var(--rule);margin-top:44px}
  .meta>div{padding:14px 16px;border-right:1px solid var(--rule)}
  .meta>div:last-child{border-right:0}
  .meta .v{font-size:13px;margin-top:7px}
  .figures{display:grid;grid-template-columns:repeat(3,1fr);border:1px solid var(--rule);border-bottom:0;margin-top:28px}
  .fig{padding:22px 18px 20px;border-bottom:1px solid var(--rule);border-right:1px solid var(--rule)}
  .fig:nth-child(3n){border-right:0}
  .fig .n{font-size:26px;letter-spacing:-.02em;margin-top:11px;line-height:1}
  .fig .c{font-size:11px;color:var(--soft);margin-top:10px;line-height:1.45}
  section.block{margin-top:44px;page-break-inside:avoid}
  .head{display:flex;justify-content:space-between;align-items:flex-end;gap:16px;flex-wrap:wrap;margin-bottom:12px}
  .card{border:1px solid var(--rule);padding:18px}
  .big{font-size:27px;letter-spacing:-.02em;line-height:1}
  .pair{display:grid;grid-template-columns:repeat(2,1fr);border:1px solid var(--rule);border-bottom:0;margin-bottom:0}
  .pair>div{padding:14px 16px;border-right:1px solid var(--rule);border-bottom:1px solid var(--rule)}
  .pair>div:last-child{border-right:0}
  .pair .big{margin-top:8px;font-size:23px}
  .keys{display:flex;gap:18px;flex-wrap:wrap;font-size:10.5px;color:var(--muted);margin-top:12px}
  .key{display:inline-flex;align-items:center;gap:7px}
  .sw{width:10px;height:10px;display:inline-block;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .swl{height:3px}
  .chart{width:100%;height:auto;display:block}
  .g{stroke:${PALETTE.grid};stroke-width:1;stroke-dasharray:2 4}
  .ax{font-family:ui-monospace,"SF Mono",SFMono-Regular,Menlo,Consolas,monospace;font-size:9.5px;fill:var(--soft)}
  .cap{font-size:12px;color:var(--muted);line-height:1.6;margin-top:10px}
  table{width:100%;border-collapse:collapse;font-size:11px;margin-top:16px}
  .tablewrap{border:1px solid var(--rule);border-top:0;padding:0 18px 14px}
  th,td{padding:6px 8px;text-align:right;border-bottom:1px solid var(--rule)}
  th:first-child,td:first-child{text-align:left}
  th{font-size:9.5px;letter-spacing:.12em;text-transform:uppercase;color:var(--muted);font-weight:400}
  tfoot td{font-weight:400;border-bottom:0;border-top:1px solid var(--ink)}
  .split{display:grid;grid-template-columns:200px 1fr;gap:26px;align-items:center}
  .donut{width:200px;height:200px}
  .lst{list-style:none;margin:0;padding:0;font-size:12.5px}
  .lst li{display:flex;justify-content:space-between;align-items:center;gap:14px;padding:9px 0;border-bottom:1px solid var(--rule)}
  .lst li:last-child{border-bottom:0}
  footer{margin-top:48px;padding-top:18px;border-top:1px solid var(--rule);font-size:12px;color:var(--muted);line-height:1.6}
  @media (max-width:680px){
    body{padding:40px 18px 64px}h1{font-size:27px}
    .meta,.figures{grid-template-columns:1fr}
    .meta>div,.fig{border-right:0}
    .split{grid-template-columns:1fr}
  }
  /* A4 / PDF. Every chart is static SVG, so printing is exact — nothing here
     has to substitute for an interactive element. */
  @page{size:A4;margin:14mm}
  @media print{
    html,body{background:#fff;padding:0}
    body{font-size:11.5px}
    .sheet{max-width:none}
    h1{font-size:26px}
    h2{font-size:16px}
    .lede{font-size:12.5px}
    .fig .n{font-size:21px}
    .big{font-size:19px}
    .pair .big{font-size:17px}
    header{break-after:avoid;page-break-after:avoid}
    h1,h2,.head,.eyebrow{break-after:avoid;page-break-after:avoid}
    section.meta{margin-top:22px}
    section.block{margin-top:16px;break-inside:auto;page-break-inside:auto}
    .card{padding:14px}
    /* A totals strip must never be the last thing on a page — it belongs with
       the chart it introduces. */
    .pair{break-after:avoid;page-break-after:avoid}
    /* Keep each chart, its legend and its caption on one page; let only the
       long month table flow across pages. */
    .card,.figures,.meta,.pair,.split,.chart,.donut{break-inside:avoid;page-break-inside:avoid}
    .tablewrap{break-inside:auto;page-break-inside:auto}
    thead{display:table-header-group}
    tfoot{display:table-row-group}
    tr,.fig{break-inside:avoid;page-break-inside:avoid}
    footer{break-before:avoid;page-break-before:avoid;margin-top:26px}
  }
</style>
</head>
<body>
<div class="sheet">
  <header>
    <div class="eyebrow">Monk Mackenzie · Employee Share Scheme</div>
    <h1>Firm-wide forecast summary</h1>
    <div class="lede">
      A point-in-time read of the practice at firm level. Every figure is taken
      live from the forecast at the moment this document was generated.
    </div>
  </header>

  <section class="meta">
    <div><div class="eyebrow">Scenario</div><div class="v">${esc(scenarioLabel)}</div></div>
    <div><div class="eyebrow">Period</div><div class="v">${esc(periodLabel)}</div></div>
    <div><div class="eyebrow">Generated</div><div class="v">${esc(stamp(generatedAt))}</div></div>
  </section>

  <section class="figures">
    ${figures
      .map(
        ([label, value, caption]) => `<div class="fig">
      <div class="eyebrow">${esc(label)}</div>
      <div class="n">${esc(value)}</div>
      <div class="c">${esc(caption)}</div>
    </div>`
      )
      .join("\n    ")}
  </section>

  <section class="block">
    <div class="head">
      <div><div class="eyebrow">By month · scenario</div><h2>Monthly revenue</h2></div>
      <div class="big mono">${esc(money(revenue.total))}</div>
    </div>
    <div class="card">
      ${lineChart({
        labels,
        band: revenue.rows.map((r) => [r.net, r.total]),
        series: [
          { values: revenue.rows.map((r) => r.total), color: PALETTE.total },
          { values: revenue.rows.map((r) => r.net), color: PALETTE.net },
        ],
      })}
      <div class="keys">
        ${swatch(PALETTE.total, "Total (gross forecast)", "line")}
        ${swatch(PALETTE.net, "Net (less sub-consultants)", "line")}
        ${swatch(PALETTE.subs, "Shaded band = sub-consultant deduction")}
      </div>
      <div class="cap">
        Total is the gross fee to clients; Net is what the firm keeps. The shaded
        band between them is the sub-consultant deduction — where the lines meet,
        there were no sub-consultant costs that month.
      </div>
    </div>
    <div class="tablewrap">
      <table>
        <thead><tr><th>Month</th><th>Total (gross)</th><th>Net</th><th>Sub-consultants</th></tr></thead>
        <tbody>
          ${revenue.rows
            .map(
              (r) => `<tr><td>${esc(r.label)}</td><td>${esc(money(r.total))}</td><td>${esc(money(r.net))}</td><td>${r.subs ? `− ${esc(money(r.subs))}` : esc(money(0))}</td></tr>`
            )
            .join("\n          ")}
        </tbody>
        <tfoot><tr><td>Total</td><td>${esc(money(revenue.rows.reduce((s, r) => s + r.total, 0)))}</td><td>${esc(money(revenue.rows.reduce((s, r) => s + r.net, 0)))}</td><td>− ${esc(money(revenue.rows.reduce((s, r) => s + r.subs, 0)))}</td></tr></tfoot>
      </table>
    </div>
  </section>

  <section class="block">
    <div class="head"><div><div class="eyebrow">Certainty over time</div><h2>Backlog vs Pipeline</h2></div></div>
    <div class="pair">
      <div><div class="eyebrow">Backlog total</div><div class="big">${esc(money(backlogPipeline.backlogTotal))}</div></div>
      <div><div class="eyebrow">Pipeline total</div><div class="big">${esc(money(backlogPipeline.pipelineTotal))}</div></div>
    </div>
    <div class="card" style="border-top:0">
      ${groupedBars({
        labels,
        testid: "backlog-pipeline",
        series: [
          { values: backlogPipeline.rows.map((r) => r.backlog), color: PALETTE.backlog },
          { values: backlogPipeline.rows.map((r) => r.pipeline), color: PALETTE.pipeline },
        ],
      })}
      <div class="keys">
        ${swatch(PALETTE.backlog, "Backlog · confirmed")}
        ${swatch(PALETTE.pipeline, "Pipeline · tentative")}
      </div>
      <div class="cap">Backlog is signed work; Pipeline is likely-but-unsigned work. Both read the same under either scenario.</div>
    </div>
  </section>

  <section class="block">
    <div class="head"><div><div class="eyebrow">Plan vs cash</div><h2>Invoiced vs Forecast</h2></div></div>
    <div class="pair">
      <div><div class="eyebrow">Forecast total</div><div class="big">${esc(money(invoiced.forecastTotal))}</div></div>
      <div><div class="eyebrow">Invoiced to date</div><div class="big">${esc(money(invoiced.invoicedTotal))}</div></div>
    </div>
    <div class="card" style="border-top:0">
      ${barsWithLine({
        labels,
        testid: "invoiced-forecast",
        bars: { values: invoiced.rows.map((r) => r.invoiced), color: PALETTE.invoiced },
        line: { values: invoiced.rows.map((r) => r.forecast), color: PALETTE.ink },
      })}
      <div class="keys">
        ${swatch(PALETTE.invoiced, "Invoiced · actual")}
        ${swatch(PALETTE.ink, "Forecast · plan", "line")}
      </div>
      <div class="cap">Forecast is the full ${esc(String(monthCount))}-month plan; invoicing catches up month by month, so the two totals are not meant to match.</div>
    </div>
  </section>

  <section class="block">
    <div class="head"><div><div class="eyebrow">Split over time</div><h2>Revenue by stage · monthly</h2></div></div>
    <div class="card">
      ${stackedBars({
        labels,
        testid: "revenue-by-stage",
        keys: stage.stages,
        rows: stage.rows,
        colors: stage.colors,
      })}
      <div class="keys">${stage.stages.map((s) => swatch(stage.colors[s] || PALETTE.soft, s)).join("\n        ")}</div>
      <div class="cap">Each bar is a month's production split by design stage. Custom scope labels roll up into these stages; anything unmatched lands in Other.</div>
    </div>
  </section>

  <section class="block">
    <div class="head"><div><div class="eyebrow">Certainty</div><h2>Contract status</h2></div></div>
    <div class="card">
      <div class="split">
        ${donut(contract, contractColors)}
        <ul class="lst">
          ${contract
            .map(
              (c) => `<li><span>${esc(contractName[c.name] || c.name)}</span><span>${esc(compact(c.value))} &nbsp;<span style="color:var(--soft)">${esc(pct(Math.max(0, c.value) / contractTotal))}</span></span></li>`
            )
            .join("\n          ")}
        </ul>
      </div>
      <div class="cap">Same ${esc(String(monthCount))}-month period as every other figure in this
        document. Confirmed is the signed backlog and Tentative what the unsigned work adds on top,
        so this split reconciles with the first two figures at the head of the document. Like those
        figures, it does not move with the selected scenario.</div>
    </div>
  </section>

  <footer>
    Firm-level totals only. This summary carries no project, client, principal or
    personnel information, and no individual is named anywhere in it.
  </footer>
</div>
</body>
</html>
`;
}

export function downloadFirmSummaryHtml(fields) {
  const html = buildFirmSummaryHtml(fields);
  const d = fields.generatedAt;
  const day = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `monk-mackenzie-firm-summary-${String(fields.scenarioLabel).toLowerCase()}-${day}.html`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
